import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit3, Trash2, X, Check } from 'lucide-react';
import unitTypesService from '../../services/unitTypesService';
import apiClient from '../../utils/apiClient';

/**
 * Unit Types rate-sheet editor. Matches the ESS Unit Types page UX:
 *
 *   Size                  Photo   Units  Pricing            Description
 *   ─────────────────────────────────────────────────────────────────────
 *   10x10                 [img]   29     $85.00 / 1 Month   Walk-in...
 *   10x15                 [img]   13     $110.00            1-bedroom...
 *   ...
 *
 * Operators define unit types ONCE per facility; every Unit row references
 * one via unit_type_id. Changing the rate here propagates to all units of
 * that type at query time.
 */

const CATEGORY_OPTIONS = [
  { value: 'enclosed', label: 'Enclosed' },
  { value: 'outdoor_parking', label: 'Outdoor Parking' },
  { value: 'covered_canopy', label: 'Covered Canopy' },
  { value: 'climate_controlled', label: 'Climate Controlled' },
];

// Operator-picked storage kind. Drives whether the public rent wizard
// presents the vehicle/boat/trailer property fields. Only the first option
// hides them; the other three show them (ESS-style — operator declares it,
// tenant never guesses).
const STORAGE_KIND_OPTIONS = [
  { value: 'non_vehicle_enclosed', label: 'Non-vehicle enclosed (household / business only)' },
  { value: 'enclosed_both', label: 'Enclosed — household/business or vehicle' },
  { value: 'outdoor', label: 'Outdoor (vehicle / boat / RV)' },
  { value: 'outdoor_both', label: 'Outdoor — vehicle + warehouse goods' },
];

const blankType = () => ({
  name: '',
  category: 'enclosed',
  // Default to the safest kind (hides vehicle fields). Operator picks the
  // right one; legacy types with no storage_kind fall back to category.
  storage_kind: 'non_vehicle_enclosed',
  monthly_rate: 0,
  description: '',
  photo_url: '',
  width: '',
  length: '',
  tpp_eligible: false,
  is_active: true,
  // Pin to a specific lease template; empty string means "fall through to
  // facility default" (mirrored as null in the API payload).
  lease_template_id: '',
  // ── Auto-generate-units wizard (CREATE only, ignored on edit) ─────
  // Default-on: when defining a new type the operator almost always
  // wants to stamp out the matching inventory rows in the same click.
  auto_generate: true,
  auto_generate_count: 10,
  auto_generate_starting_number: '', // blank => server continues from highest existing
  auto_generate_prefix: '',
});

const UnitTypesPanel = ({ facilityId }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null); // 'new' for create, type._id for edit
  const [draft, setDraft] = useState(blankType());
  const [saving, setSaving] = useState(false);
  // Active templates for the lease_template_id dropdown. Loaded once
  // alongside the types so the form doesn't flicker when opened.
  const [leaseTemplates, setLeaseTemplates] = useState([]);
  // Success banner shown after a create that auto-generated units.
  // Auto-dismisses after ~6s so it doesn't linger forever.
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [res, tplRes] = await Promise.all([
        unitTypesService.getAll(facilityId, { include_inactive: true }),
        apiClient
          .get(`/api/facilities/${facilityId}/lease-templates/`)
          .catch(() => ({ data: [] })),
      ]);
      setTypes(res.data || []);
      // Only the active templates surface in the dropdown — inactive
      // rows shouldn't be assignable. The default-first sort from the
      // backend is preserved.
      setLeaseTemplates((tplRes.data || []).filter((t) => t.is_active !== false));
    } catch (err) {
      console.error('UnitTypesPanel load error:', err);
      setError('Could not load unit types.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => {
    setDraft(blankType());
    setEditingId('new');
  };

  const startEdit = (t) => {
    setDraft({
      name: t.name || '',
      category: t.category || 'enclosed',
      // Fall back to a category-derived default for legacy types that have
      // no storage_kind yet, so the dropdown shows a sensible pre-selection.
      storage_kind: t.storage_kind
        || (((t.category || '').toLowerCase() === 'outdoor_parking')
              ? 'outdoor' : 'non_vehicle_enclosed'),
      monthly_rate: t.monthly_rate ?? 0,
      description: t.description || '',
      photo_url: t.photo_url || '',
      width: t.width ?? '',
      length: t.length ?? '',
      tpp_eligible: !!t.tpp_eligible,
      is_active: t.is_active !== false,
      lease_template_id: t.lease_template_id || '',
      // ── Add-units control (EDIT only) ──────────────────────────────
      // Off by default — an edit usually just tweaks fields. The current
      // unit_count drives the "Currently N units" label; the rest mirror
      // the create wizard's bulk-stamp inputs and ride the same PUT call.
      current_unit_count: t.unit_count ?? 0,
      add_units: false,
      auto_generate_count: 1,
      auto_generate_starting_number: '', // blank => server continues numbering
      auto_generate_prefix: '',
    });
    setEditingId(t._id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(blankType());
  };

  const save = async () => {
    const payload = {
      name: draft.name.trim(),
      category: draft.category,
      storage_kind: draft.storage_kind || null,
      monthly_rate: Number(draft.monthly_rate) || 0,
      description: draft.description.trim() || null,
      photo_url: draft.photo_url.trim() || null,
      width: draft.width === '' ? null : Number(draft.width),
      length: draft.length === '' ? null : Number(draft.length),
      tpp_eligible: !!draft.tpp_eligible,
      is_active: !!draft.is_active,
      // Empty string => "use facility default" => null on the wire.
      lease_template_id: draft.lease_template_id || null,
    };
    if (!payload.name) {
      alert('Name is required.');
      return;
    }
    // Attach auto-generate hints on CREATE (when the wizard checkbox is on)
    // and on EDIT (when the "Add units" control is on). Both paths feed the
    // same backend bulk-stamp via UnitTypeCreate/UnitTypeUpdate, which now
    // both declare the auto_generate_* fields.
    const wantsAutogen =
      (editingId === 'new' && draft.auto_generate) ||
      (editingId !== 'new' && draft.add_units);
    if (wantsAutogen) {
      const cnt = parseInt(draft.auto_generate_count, 10);
      if (!Number.isFinite(cnt) || cnt < 1) {
        alert(
          editingId === 'new'
            ? 'Number of units must be at least 1 (or uncheck Auto-generate).'
            : 'Number of units to add must be at least 1 (or uncheck Add units).',
        );
        return;
      }
      payload.auto_generate_count = cnt;
      if (draft.auto_generate_starting_number !== '') {
        const start = parseInt(draft.auto_generate_starting_number, 10);
        if (Number.isFinite(start) && start >= 0) {
          payload.auto_generate_starting_number = start;
        }
      }
      if (draft.auto_generate_prefix) {
        payload.auto_generate_prefix = draft.auto_generate_prefix;
      }
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        const res = await unitTypesService.create(facilityId, payload);
        const generated = res?.data?.units_generated || 0;
        const range = res?.data?.generated_range;
        if (generated > 0) {
          const rangeStr = range
            ? ` (numbered ${range.first}-${range.last})`
            : '';
          setBanner(
            `Created unit type "${payload.name}" + ${generated} unit${
              generated === 1 ? '' : 's'
            }${rangeStr}.`,
          );
        } else {
          setBanner(`Created unit type "${payload.name}".`);
        }
        // Auto-dismiss banner; operator can also click X.
        setTimeout(() => setBanner(null), 6000);
      } else {
        const res = await unitTypesService.update(facilityId, editingId, payload);
        const added = res?.data?.units_generated || 0;
        const range = res?.data?.generated_range;
        if (added > 0) {
          const rangeStr = range ? ` (${range.first}–${range.last})` : '';
          setBanner(
            `Added ${added} unit${added === 1 ? '' : 's'} to "${payload.name}"${rangeStr}.`,
          );
          setTimeout(() => setBanner(null), 6000);
        }
      }
      cancelEdit();
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (t.unit_count > 0) {
      alert(
        `Cannot delete "${t.name}": ${t.unit_count} unit(s) still reference this type. Reassign or archive them first.`,
      );
      return;
    }
    if (!window.confirm(`Delete unit type "${t.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await unitTypesService.delete(facilityId, t._id);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-[13px] text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div>
      {banner && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[13px] text-emerald-800">
          <span>{banner}</span>
          <button
            onClick={() => setBanner(null)}
            className="text-emerald-700 hover:text-emerald-900"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <p className="text-[13px] text-slate-500">
          Define each kind of unit you rent once. Every Unit references one
          Unit Type; changing the rate here updates every unit of that type.
        </p>
        {editingId === null && (
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            New Unit Type
          </button>
        )}
      </div>

      {/* Inline create form */}
      {editingId === 'new' && (
        <UnitTypeForm
          draft={draft}
          setDraft={setDraft}
          onCancel={cancelEdit}
          onSave={save}
          saving={saving}
          leaseTemplates={leaseTemplates}
          isNew
        />
      )}

      {/* Types table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Name</th>
                <th className="text-left font-medium px-4 py-2.5">Category</th>
                <th className="text-right font-medium px-4 py-2.5">Units</th>
                <th className="text-right font-medium px-4 py-2.5">Rate</th>
                <th className="text-left font-medium px-4 py-2.5">Description</th>
                <th className="text-left font-medium px-4 py-2.5">Lease</th>
                <th className="text-center font-medium px-4 py-2.5">TPP</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {types.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-slate-400"
                  >
                    No unit types yet. Click "New Unit Type" to define your
                    first one.
                  </td>
                </tr>
              )}
              {types.map((t) =>
                editingId === t._id ? (
                  <tr key={t._id} className="bg-slate-50">
                    <td colSpan={8} className="p-4">
                      <UnitTypeForm
                        draft={draft}
                        setDraft={setDraft}
                        onCancel={cancelEdit}
                        onSave={save}
                        saving={saving}
                        leaseTemplates={leaseTemplates}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={t._id} className={!t.is_active ? 'opacity-50' : ''}>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {t.name}
                      {!t.is_active && (
                        <span className="ml-2 text-[10px] uppercase text-slate-400">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {CATEGORY_OPTIONS.find((c) => c.value === t.category)
                        ?.label || t.category}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular-nums">
                      {t.unit_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                      ${Number(t.monthly_rate || 0).toFixed(2)}
                      <span className="text-slate-400 font-normal text-[11px]">
                        /mo
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 max-w-md">
                      <div className="line-clamp-2">{t.description || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px]">
                      {(() => {
                        const pinned = leaseTemplates.find((x) => x._id === t.lease_template_id);
                        const fallback = leaseTemplates.find((x) => x.is_default);
                        if (pinned) {
                          return <span className="truncate block" title={pinned.name}>{pinned.name}</span>;
                        }
                        if (fallback) {
                          return (
                            <span className="text-slate-400 italic truncate block" title={`Facility default: ${fallback.name}`}>
                              Default ({fallback.name})
                            </span>
                          );
                        }
                        return <span className="text-amber-700 text-[12px]">Not configured</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {t.tpp_eligible ? (
                        <Check className="h-4 w-4 text-emerald-600 inline" />
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => startEdit(t)}
                          className="text-slate-400 hover:text-blue-600"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => remove(t)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                          disabled={(t.unit_count ?? 0) > 0}
                          title={
                            (t.unit_count ?? 0) > 0
                              ? `Reassign or archive ${t.unit_count} unit(s) first`
                              : 'Delete'
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── inline create/edit form ───────────────────────────────────────────

const UnitTypeForm = ({
  draft, setDraft, onCancel, onSave, saving, isNew,
  leaseTemplates = [],
}) => {
  const set = (field) => (e) => {
    const v =
      e?.target?.type === 'checkbox'
        ? e.target.checked
        : e?.target?.value ?? e;
    setDraft((d) => ({ ...d, [field]: v }));
  };
  return (
    <div
      className={`${
        isNew ? 'rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4' : ''
      } space-y-3`}
    >
      {isNew && (
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[14px] font-semibold text-slate-900">
            New Unit Type
          </h3>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-700"
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Name *">
          <input
            value={draft.name}
            onChange={set('name')}
            placeholder="e.g. 10x10"
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:border-blue-500"
          />
        </Field>
        <Field label="Category *">
          <select
            value={draft.category}
            onChange={set('category')}
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] bg-white"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Storage kind *">
          <select
            value={draft.storage_kind}
            onChange={set('storage_kind')}
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] bg-white"
          >
            {STORAGE_KIND_OPTIONS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-400">
            Controls whether the online rent form asks for vehicle/boat/trailer
            details. Only “Non-vehicle enclosed” hides those fields.
          </p>
        </Field>
        <Field label="Monthly rate *">
          <div className="relative">
            <span className="absolute left-3 top-1.5 text-slate-400">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.monthly_rate}
              onChange={set('monthly_rate')}
              className="w-full pl-6 pr-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums focus:outline-none focus:border-blue-500"
            />
          </div>
        </Field>
        <Field label="Width (ft, optional)">
          <input
            type="number"
            step="0.5"
            min="0"
            value={draft.width}
            onChange={set('width')}
            placeholder="10"
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums focus:outline-none focus:border-blue-500"
          />
        </Field>
        <Field label="Length (ft, optional)">
          <input
            type="number"
            step="0.5"
            min="0"
            value={draft.length}
            onChange={set('length')}
            placeholder="10"
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums focus:outline-none focus:border-blue-500"
          />
        </Field>
        <Field label="Photo URL (optional)">
          <input
            value={draft.photo_url}
            onChange={set('photo_url')}
            placeholder="https://…/10x10.jpg"
            className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:border-blue-500"
          />
        </Field>
      </div>
      <Field label="Description (shown on public rent page)">
        <textarea
          value={draft.description}
          onChange={set('description')}
          rows={3}
          placeholder="Walk-in closet to small bedroom. 1-bedroom apartment contents."
          className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:border-blue-500"
        />
      </Field>
      <Field label="Lease template">
        <select
          value={draft.lease_template_id || ''}
          onChange={set('lease_template_id')}
          className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] bg-white"
        >
          <option value="">Use facility default</option>
          {leaseTemplates.map((t) => (
            <option key={t._id} value={t._id}>
              {t.name}{t.is_default ? ' (default)' : ''}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-slate-400 mt-1">
          Tip: pin a specific template here for unit types that need custom
          lease language. Otherwise the facility default applies.
        </p>
        {(() => {
          const pinned = leaseTemplates.find(
            (t) => t._id === draft.lease_template_id,
          );
          const fallback = leaseTemplates.find((t) => t.is_default);
          const using = pinned || fallback;
          if (!using) {
            return (
              <p className="text-[11px] text-amber-700 mt-1">
                No facility default set. Configure one in
                Settings &gt; Storage Agreements.
              </p>
            );
          }
          return (
            <p className="text-[11px] text-slate-500 mt-1">
              Currently using: <span className="font-medium">{using.name}</span>
              {!pinned && <span className="text-slate-400"> (facility default)</span>}
            </p>
          );
        })()}
      </Field>
      <div className="flex items-center gap-5">
        <label className="inline-flex items-center gap-2 text-[13px] text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.tpp_eligible}
            onChange={set('tpp_eligible')}
            className="rounded border-slate-300"
          />
          TPP eligible (tenant protection plan)
        </label>
        <label className="inline-flex items-center gap-2 text-[13px] text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={set('is_active')}
            className="rounded border-slate-300"
          />
          Show on public rent page
        </label>
      </div>

      {/* ── Auto-generate units section (CREATE only) ─────────────── */}
      {isNew && (
        <AutoGenerateUnitsSection draft={draft} setDraft={setDraft} />
      )}

      {/* ── Add units section (EDIT only) ─────────────────────────── */}
      {!isNew && <AddUnitsSection draft={draft} setDraft={setDraft} />}

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-1.5 rounded-md bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save changes'}
        </button>
        <button
          onClick={onCancel}
          className="text-[13px] text-slate-500 hover:text-slate-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Auto-generate units section (CREATE only) ────────────────────────
//
// Sits inside UnitTypeForm but only renders when isNew is true. The
// preview helper formats the resulting unit_numbers so the operator
// sees exactly what will be created BEFORE submitting.
//
// Preview rules:
//   - <= 7 numbers: list them all
//   - > 7: first 5, ellipsis, last 2 (e.g. "1, 2, 3, 4, 5 ... 99, 100")
const buildAutoGeneratePreview = (count, start, prefix) => {
  const n = parseInt(count, 10);
  if (!Number.isFinite(n) || n < 1) return '';
  const s = start === '' || start === null || start === undefined
    ? null
    : parseInt(start, 10);
  // When the operator hasn't specified a starting number, default the
  // preview to 1 — the server will continue from the highest existing
  // numeric unit_number, but at form time we don't know what that is.
  const firstNum = Number.isFinite(s) ? s : 1;
  const p = prefix || '';
  const all = [];
  for (let i = 0; i < n; i += 1) all.push(`${p}${firstNum + i}`);
  if (all.length <= 7) return all.join(', ');
  return `${all.slice(0, 5).join(', ')} ... ${all.slice(-2).join(', ')}`;
};

const AutoGenerateUnitsSection = ({ draft, setDraft }) => {
  const toggle = (e) =>
    setDraft((d) => ({ ...d, auto_generate: e.target.checked }));
  const updateField = (field) => (e) =>
    setDraft((d) => ({ ...d, [field]: e.target.value }));
  const preview = buildAutoGeneratePreview(
    draft.auto_generate_count,
    draft.auto_generate_starting_number,
    draft.auto_generate_prefix,
  );
  const startIsBlank =
    draft.auto_generate_starting_number === '' ||
    draft.auto_generate_starting_number === null ||
    draft.auto_generate_starting_number === undefined;
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-3">
      <label className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          checked={!!draft.auto_generate}
          onChange={toggle}
          className="rounded border-slate-300"
        />
        Auto-generate units now
      </label>
      {draft.auto_generate && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Number of units *">
              <input
                type="number"
                min="1"
                max="500"
                value={draft.auto_generate_count}
                onChange={updateField('auto_generate_count')}
                className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums bg-white focus:outline-none focus:border-blue-500"
              />
            </Field>
            <Field label="Starting number (optional)">
              <input
                type="number"
                min="0"
                value={draft.auto_generate_starting_number}
                onChange={updateField('auto_generate_starting_number')}
                placeholder="next available"
                className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums bg-white focus:outline-none focus:border-blue-500"
              />
            </Field>
            <Field label="Number prefix (optional)">
              <input
                value={draft.auto_generate_prefix}
                onChange={updateField('auto_generate_prefix')}
                placeholder='e.g. "A-"'
                className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] bg-white focus:outline-none focus:border-blue-500"
              />
            </Field>
          </div>
          <div className="text-[12px] text-slate-600">
            {preview ? (
              <>
                <span className="font-medium">Preview:</span> will create
                unit numbers: <span className="tabular-nums">{preview}</span>
                {startIsBlank && (
                  <span className="text-slate-400">
                    {' '}
                    (or continued from your highest existing numeric unit)
                  </span>
                )}
              </>
            ) : (
              <span className="text-slate-400">
                Enter a count to preview the unit numbers that will be created.
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            You can rename individual units later from the Units grid to match
            your physical signage.
          </p>
        </>
      )}
      {!draft.auto_generate && (
        <p className="text-[12px] text-slate-500">
          Just the unit type will be created. Add units later from the Units
          page (useful when defining a type for future expansion).
        </p>
      )}
    </div>
  );
};

// ── Add units section (EDIT only) ────────────────────────────────────
//
// Lets an operator increase an existing type's unit count after creation.
// Reuses the create wizard's bulk-stamp inputs + preview and rides the same
// PUT update call (auto_generate_* now declared on UnitTypeUpdate too). The
// new numbers continue from the facility's highest existing numeric unit
// unless the operator pins a starting number.
const AddUnitsSection = ({ draft, setDraft }) => {
  const toggle = (e) =>
    setDraft((d) => ({ ...d, add_units: e.target.checked }));
  const updateField = (field) => (e) =>
    setDraft((d) => ({ ...d, [field]: e.target.value }));
  const preview = buildAutoGeneratePreview(
    draft.auto_generate_count,
    draft.auto_generate_starting_number,
    draft.auto_generate_prefix,
  );
  const startIsBlank =
    draft.auto_generate_starting_number === '' ||
    draft.auto_generate_starting_number === null ||
    draft.auto_generate_starting_number === undefined;
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={!!draft.add_units}
            onChange={toggle}
            className="rounded border-slate-300"
          />
          Add units to this type
        </label>
        <span className="text-[12px] text-slate-500 tabular-nums">
          Currently {draft.current_unit_count ?? 0} unit
          {(draft.current_unit_count ?? 0) === 1 ? '' : 's'}
        </span>
      </div>
      {draft.add_units && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Units to add *">
              <input
                type="number"
                min="1"
                max="500"
                value={draft.auto_generate_count}
                onChange={updateField('auto_generate_count')}
                className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums bg-white focus:outline-none focus:border-blue-500"
              />
            </Field>
            <Field label="Starting number (optional)">
              <input
                type="number"
                min="0"
                value={draft.auto_generate_starting_number}
                onChange={updateField('auto_generate_starting_number')}
                placeholder="next available"
                className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] tabular-nums bg-white focus:outline-none focus:border-blue-500"
              />
            </Field>
            <Field label="Number prefix (optional)">
              <input
                value={draft.auto_generate_prefix}
                onChange={updateField('auto_generate_prefix')}
                placeholder='e.g. "A-"'
                className="w-full px-3 py-1.5 rounded-md border border-slate-200 text-[13px] bg-white focus:outline-none focus:border-blue-500"
              />
            </Field>
          </div>
          <div className="text-[12px] text-slate-600">
            {preview ? (
              <>
                <span className="font-medium">Preview:</span> will add unit
                numbers: <span className="tabular-nums">{preview}</span>
                {startIsBlank && (
                  <span className="text-slate-400">
                    {' '}
                    (continued from your highest existing numeric unit)
                  </span>
                )}
              </>
            ) : (
              <span className="text-slate-400">
                Enter a count to preview the unit numbers that will be added.
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            If a number in the range already exists you'll get a conflict —
            adjust the starting number or prefix and retry.
          </p>
        </>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 block">
      {label}
    </span>
    {children}
  </label>
);

export default UnitTypesPanel;
